import { useCallback } from 'react';
import { useMutation } from 'react-relay';
import { useToast } from '@pv-frontend/ui-components-web';
import type {
    proGrantMutationsGrantProMutation as GrantProMutation,
} from './__generated__/proGrantMutationsGrantProMutation.graphql';
import type {
    proGrantMutationsRevokeProMutation as RevokeProMutation,
} from './__generated__/proGrantMutationsRevokeProMutation.graphql';
import { grantProMutation, revokeProMutation } from './pro-grant-mutations';
import { proGrantErrorCopy } from './pro-grant-errors';
import type { GrantProAction, RevokeProAction } from '../screens/complimentary-pro-panel';

export interface ProGrantActions {
    onGrantPro: GrantProAction;
    onRevokePro: RevokeProAction;
}

/**
 * Binds the `grantPro` / `revokePro` mutations to the vault detail's confirm step. Each commit resolves
 * `null` once the server confirms it — toasting and asking the screen to refetch, since neither payload
 * carries the grant facts the detail shows — or readable refusal copy, which the confirm step keeps on
 * screen. Not optimistic: who is comped is server-authoritative, and a paying vault is refused.
 */
export function useProGrantActions(vaultId: string, onConfirmed: () => void): ProGrantActions {
    const toast = useToast();
    const [commitGrant] = useMutation<GrantProMutation>(grantProMutation);
    const [commitRevoke] = useMutation<RevokeProMutation>(revokeProMutation);

    const onGrantPro = useCallback<GrantProAction>(
        (term, reason) =>
            new Promise((resolve) => {
                commitGrant({
                    variables: { vaultId, term, reason },
                    onCompleted: (_response, errors) => {
                        if (errors && errors.length > 0) {
                            resolve(proGrantErrorCopy(errors, 'grant'));
                            return;
                        }
                        toast.success('Complimentary Pro granted');
                        onConfirmed();
                        resolve(null);
                    },
                    onError: (error) => resolve(proGrantErrorCopy(error, 'grant')),
                });
            }),
        [commitGrant, onConfirmed, toast, vaultId],
    );

    const onRevokePro = useCallback<RevokeProAction>(
        (reason) =>
            new Promise((resolve) => {
                commitRevoke({
                    variables: { vaultId, reason },
                    onCompleted: (_response, errors) => {
                        if (errors && errors.length > 0) {
                            resolve(proGrantErrorCopy(errors, 'revoke'));
                            return;
                        }
                        toast.success('Complimentary Pro revoked');
                        onConfirmed();
                        resolve(null);
                    },
                    onError: (error) => resolve(proGrantErrorCopy(error, 'revoke')),
                });
            }),
        [commitRevoke, onConfirmed, toast, vaultId],
    );

    return { onGrantPro, onRevokePro };
}
